import { z } from 'zod';
import { artifactSchema, citationSchema } from './artifacts.js';
import { isoDateTime } from './common.js';
import { answerKindSchema, llmUsageSchema, questionOptionSchema } from './domain.js';
/**
 * The panel's chat (core/ports/conversation.port.ts). Asserted against the core
 * types in contract-types.test.ts.
 */
export const chatAttachmentRefSchema = z.object({
    id: z.string(),
    name: z.string(),
    mime: z.string(),
    size: z.number().int(),
});
export const chatMessageSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    role: z.enum(['user', 'agent']),
    text: z.string(),
    kind: answerKindSchema.optional(),
    citations: z.array(citationSchema),
    artifact: artifactSchema.optional(),
    usage: llmUsageSchema.optional(),
    attachments: z.array(chatAttachmentRefSchema),
    createdAt: isoDateTime,
});
export const conversationSchema = z.object({
    id: z.string(),
    owner: z.object({ id: z.string(), name: z.string() }),
    title: z.string(),
    scope: z.array(questionOptionSchema),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
});
/** A list row: no creation time, which the list does not show. */
export const conversationSummarySchema = conversationSchema.omit({ createdAt: true });
export const chatSendSchema = z.object({
    /** Absent: a new conversation, titled by this message. */
    conversationId: z.uuid().optional(),
    text: z.string().trim().min(1).max(2000),
    scope: z
        .array(questionOptionSchema)
        .min(1)
        .max(5)
        // A source ticked twice is still one source.
        .transform((options) => [...new Set(options)]),
    attachmentIds: z.array(z.uuid()).max(3).optional(),
});
/**
 * One server-sent event on POST /chat/stream, each written as
 * `data: <json>\n\n`. In order: `conversation`, then `step`s, then one `delta`
 * with the whole text (the provider documents no token streaming), then
 * `done` with the stored message — or `error` at any point.
 */
export const chatStreamEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('conversation'), conversationId: z.string(), title: z.string() }),
    z.object({
        type: z.literal('step'),
        step: z.enum(['reading', 'searching', 'tool', 'answering']),
        option: questionOptionSchema.optional(),
        label: z.string().optional(),
        /** `tool` only: what the agent is fetching — "HOS clocks". */
        tool: z.string().optional(),
        subject: z.string().optional(),
        records: z.number().int().optional(),
    }),
    z.object({ type: z.literal('delta'), text: z.string() }),
    z.object({ type: z.literal('done'), message: chatMessageSchema }),
    z.object({ type: z.literal('error'), error: z.string(), message: z.string() }),
]);
export const uploadedAttachmentSchema = z.object({
    id: z.string(),
    name: z.string(),
    mime: z.string(),
    size: z.number().int(),
    /** Characters of text extracted. */
    chars: z.number().int(),
});
//# sourceMappingURL=chat.js.map